import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Mail, Send, Link2, Loader2, ExternalLink, Search, Unlink, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { value: "all", label: "All" },
  { value: "standalone", label: "Not linked" },
  { value: "linked", label: "Linked to lead" },
];

function fmt(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

export default function SentEmailsPage() {
  const navigate = useNavigate();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [linkDialog, setLinkDialog] = useState(null); // {email_id, subject}
  const [openEmail, setOpenEmail] = useState(null);

  const fetchSent = async (f = filter) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/email-composer/sent?filter=${f}&limit=200`, {
        credentials: "include",
      });
      const data = await res.json();
      setEmails(data.emails || []);
    } catch (e) {
      toast.error("Failed to load sent emails");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSent(filter); }, [filter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return emails;
    return emails.filter((e) =>
      (e.subject || "").toLowerCase().includes(q)
      || (e.to_email || "").toLowerCase().includes(q)
      || (e.lead_business_name || "").toLowerCase().includes(q)
    );
  }, [emails, search]);

  const unlink = async (email_id) => {
    try {
      const res = await fetch(`${API}/email-composer/sent/${email_id}/unlink`, {
        method: "PUT", credentials: "include",
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "Unlink failed");
      }
      toast.success("Lead unlinked");
      fetchSent();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-4" data-testid="sent-emails-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl flex items-center gap-2">
            <Send className="w-6 h-6 text-blue-600" />
            Sent Emails
          </h1>
          <p className="text-sm text-slate-500">Every email you've sent — link any of them to a lead retroactively.</p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex gap-1.5">
              {FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setFilter(f.value)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                    filter === f.value
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid={`filter-${f.value}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search subject, recipient, lead…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
                data-testid="sent-search-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500" data-testid="empty-state">
              <Mail className="w-10 h-10 mx-auto text-slate-300 mb-2" />
              <p className="text-sm">No emails to show in this view.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="sent-list">
              {filtered.map((e) => (
                <li
                  key={e.email_id}
                  className="py-3 flex items-start justify-between gap-3 hover:bg-slate-50 -mx-3 px-3 rounded cursor-pointer"
                  onClick={() => setOpenEmail(e)}
                  data-testid={`sent-row-${e.email_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <p className="font-medium text-slate-900 truncate">{e.subject || "(no subject)"}</p>
                      {e.opened_at && (
                        <Badge
                          className="bg-green-100 text-green-700 text-[10px] gap-0.5"
                          title={`First opened ${fmt(e.opened_at)}${e.open_count > 1 ? ` · ${e.open_count} total opens` : ""}`}
                          data-testid={`opened-badge-${e.email_id}`}
                        >
                          <Eye className="w-3 h-3" />
                          Opened
                          {e.open_count > 1 && <span className="ml-0.5">×{e.open_count}</span>}
                        </Badge>
                      )}
                      {e.lead_id ? (
                        <Badge
                          className="bg-blue-100 text-blue-700 text-[10px] cursor-pointer"
                          onClick={(ev) => { ev.stopPropagation(); navigate(`/leads/${e.lead_id}`); }}
                          data-testid={`lead-chip-${e.email_id}`}
                        >
                          <Link2 className="w-3 h-3 mr-1" />
                          {e.lead_business_name || "Linked lead"}
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-500 text-[10px]">Not linked</Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">
                      To: {e.to_email}
                      {e.cc?.length > 0 && ` · Cc: ${e.cc.length}`}
                      {e.bcc?.length > 0 && ` · Bcc: ${e.bcc.length}`}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">{fmt(e.sent_at)}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {e.lead_id ? (
                      <>
                        <Button
                          size="sm" variant="ghost"
                          className="h-7 px-2 text-xs"
                          onClick={(ev) => { ev.stopPropagation(); navigate(`/leads/${e.lead_id}`); }}
                          data-testid={`open-lead-${e.email_id}`}
                        >
                          <ExternalLink className="w-3 h-3 mr-1" /> Open
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          className="h-7 px-2 text-xs text-slate-500 hover:text-red-600"
                          onClick={(ev) => { ev.stopPropagation(); unlink(e.email_id); }}
                          data-testid={`unlink-${e.email_id}`}
                          title="Unlink from lead"
                        >
                          <Unlink className="w-3 h-3" />
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        className="h-7 px-3 text-xs"
                        onClick={(ev) => { ev.stopPropagation(); setLinkDialog({ email_id: e.email_id, subject: e.subject }); }}
                        data-testid={`link-btn-${e.email_id}`}
                      >
                        <Link2 className="w-3 h-3 mr-1" />
                        Link to lead
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Link to lead dialog */}
      <LinkLeadDialog
        meta={linkDialog}
        onClose={() => setLinkDialog(null)}
        onLinked={() => { setLinkDialog(null); fetchSent(); }}
      />

      {/* View email dialog */}
      <Dialog open={!!openEmail} onOpenChange={(v) => !v && setOpenEmail(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="email-detail-dialog">
          {openEmail && (
            <>
              <DialogHeader>
                <DialogTitle className="break-words">{openEmail.subject || "(no subject)"}</DialogTitle>
                <DialogDescription className="text-xs text-slate-500">
                  Sent {fmt(openEmail.sent_at)}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 text-sm">
                <div><span className="text-slate-500">To: </span>{openEmail.to_email}</div>
                {openEmail.cc?.length > 0 && <div><span className="text-slate-500">Cc: </span>{openEmail.cc.join(", ")}</div>}
                {openEmail.bcc?.length > 0 && <div><span className="text-slate-500">Bcc: </span>{openEmail.bcc.join(", ")}</div>}
                <div className="pt-3 mt-2 border-t whitespace-pre-wrap text-slate-800">{openEmail.body}</div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LinkLeadDialog({ meta, onClose, onLinked }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (!meta) { setQuery(""); setResults([]); return; }
    const q = query.trim();
    if (!q) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`${API}/email-composer/lead-search?q=${encodeURIComponent(q)}&limit=10`, {
          credentials: "include",
        });
        if (res.ok) {
          const data = await res.json();
          setResults(data.leads || []);
        }
      } finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [query, meta]);

  const link = async (lead) => {
    setLinking(true);
    try {
      const res = await fetch(`${API}/email-composer/sent/${meta.email_id}/link`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lead_id: lead.lead_id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Link failed");
      toast.success(`Linked to ${data.lead_business_name || lead.business_name}`);
      onLinked();
    } catch (e) {
      toast.error(e.message);
    } finally { setLinking(false); }
  };

  return (
    <Dialog open={!!meta} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="link-lead-dialog">
        <DialogHeader>
          <DialogTitle>Link email to a lead</DialogTitle>
          <DialogDescription>
            {meta?.subject ? <>"{meta.subject}"</> : null}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            placeholder="Search business or contact name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
            data-testid="link-search-input"
          />
          {searching && (
            <div className="text-xs text-slate-500 flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> searching…
            </div>
          )}
          {!searching && query && results.length === 0 && (
            <p className="text-xs text-slate-400">No leads match "{query}"</p>
          )}
          <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
            {results.map((l) => (
              <button
                key={l.lead_id}
                type="button"
                disabled={linking}
                onClick={() => link(l)}
                className="w-full text-left py-2 px-2 hover:bg-slate-50 rounded disabled:opacity-50"
                data-testid={`link-result-${l.lead_id}`}
              >
                <div className="text-sm font-medium text-slate-900">{l.business_name}</div>
                <div className="text-xs text-slate-500">
                  {[l.contact_name, l.city, l.state].filter(Boolean).join(" · ")}
                </div>
              </button>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={linking}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
